// =============================================================================
// domain/leakLoop.ts — the WRITER for Fix it / Prove it.
// -----------------------------------------------------------------------------
// Everything here is a pure transform: old flags in, NEW flags out. Nothing
// mutates, nothing reaches the network, nothing reads a clock. The io layer does
// one `savePreferences({ appFlags })` with whatever comes back.
//
// ── 🚨 1. THE ONE WAY THIS FEATURE CAN DESTROY DATA ──
//
// Rules live at `preferences.app_flags.leakLoop`. That column is ONE JSONB blob
// shared by every surface in both clients, and it also holds `settingsLayout`,
// `journalLayout`, `tradeFormLayout`, `plannedTargetUnit`, the toolbar-tour flag
// and whatever the old client adds next. A save writes the WHOLE column.
//
// So a writer that builds `{ leakLoop: … }` and saves it does not "add a rule".
// It deletes every other flag the trader has ever set, from a screen that never
// mentioned any of them. Every function below spreads what was stored — the
// flags object, the loop object, AND each individual rule object — so an
// unrecognised key at any of the three levels survives untouched.
//
// ⚠ THAT IS ALSO WHY THE WRITER READS THE **RAW** ARRAY, NOT `activeLeakRules()`.
// The parsed view drops a rule with no text and narrows every entry to five
// known fields. Writing that view back would silently delete both. The parse is
// for rendering; the write starts from what is on disk.
//
// ── 🚨 2. `null` FLAGS MEAN "WE HAVE NOT READ THEM", AND MUST NOT BE WRITTEN ──
//
// `usePreferences` deliberately keeps `update()` callable while a read has
// FAILED, because a patch normally carries only the field being changed and
// cannot clobber what it never saw. This column is the exception: the patch here
// IS the whole blob, so writing it after a failed read would save `{ leakLoop }`
// over everything else — the factory-reset shape that module exists to prevent.
// The caller must refuse; `ui/state/useLeakRules.ts` is where that gate lives.
//
// ── ⚠ 3. THE SHAPE IS THE OLD CLIENT'S, EXACTLY ──
//
// Both clients read the same beta row. A rule written here has to be one the old
// client can open, prove and bank — so the keys, the `status` strings and the
// nested baseline match its `createLeakRule` / `bankRule` / `retireRule` field
// for field, including `baseline.guardrail.metric`, which nothing reads back and
// which the old client writes anyway.
// =============================================================================

import type { LeakBaseline, LeakRule } from './leakRules';
import { baselineUsable } from './leakRules';
import { leakById, realizedExpectancy, type LeakContext, type LeakTrade } from './leaks';

/** Where the loop lives inside the shared flags blob. */
export const LEAK_LOOP_KEY = 'leakLoop';

/** Every leak in both clients is certified against the same guardrail. */
const GUARDRAIL_METRIC = 'expectancy';

export type RuleStatus = 'active' | 'banked' | 'retired';

/** A rule that has left the active list, as the ledger renders it. */
export interface LedgerRule extends LeakRule {
  status: RuleStatus;
  bankedAt: string | null;
  retiredAt: string | null;
  reason: string;
}

// ---------------------------------------------------------------------------
// Reading the raw blob
// ---------------------------------------------------------------------------

const asObject = (v: unknown): Record<string, unknown> | null =>
  (v && typeof v === 'object' && !Array.isArray(v)) ? v as Record<string, unknown> : null;

/**
 * The stored loop, as objects rather than as a parsed view.
 *
 * ⚠ REPRODUCES THE OLD CLIENT'S MIGRATION. Before rules could run several at
 * once the shape was `{ active: <rule|null> }`; beta folds that single rule into
 * `actives` and deletes the old key on every read. Doing the same here matters
 * for a reason the read-only path does not have: if the writer left `active`
 * in place, the same rule would exist twice — once where this client looks and
 * once where an older one does — and banking it in one place would leave the
 * other copy running.
 */
export function rawLeakLoop(
  appFlags: Record<string, unknown> | null | undefined,
): { loop: Record<string, unknown>; actives: unknown[]; ledger: unknown[] } {
  const loop = asObject((appFlags ?? {})[LEAK_LOOP_KEY]) ?? {};
  const actives = Array.isArray(loop['actives'])
    ? [...(loop['actives'] as unknown[])]
    : (asObject(loop['active']) ? [loop['active']] : []);
  const ledger = Array.isArray(loop['ledger']) ? [...(loop['ledger'] as unknown[])] : [];
  return { loop, actives, ledger };
}

/**
 * Put a loop back into the flags blob.
 *
 * ⚠ THREE SPREADS, AND EVERY ONE OF THEM IS LOAD-BEARING — the flags object so
 * other surfaces' settings survive, the loop object so a key this client does
 * not know survives, and the deletion of the legacy `active` so the migration
 * above actually lands.
 */
function withLoop(
  appFlags: Record<string, unknown> | null | undefined,
  loop: Record<string, unknown>,
  actives: unknown[],
  ledger: unknown[],
): Record<string, unknown> {
  const nextLoop: Record<string, unknown> = { ...loop, actives, ledger };
  delete nextLoop['active'];
  return { ...(appFlags ?? {}), [LEAK_LOOP_KEY]: nextLoop };
}

/** The id on a raw stored rule, however it was written. */
const idOf = (raw: unknown): string => String(asObject(raw)?.['id'] ?? '');
const leakIdOf = (raw: unknown): string => String(asObject(raw)?.['leakId'] ?? '');

// ---------------------------------------------------------------------------
// The ledger, for rendering
// ---------------------------------------------------------------------------

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Every rule the trader has finished with, newest first.
 *
 * ⚠ A LEDGER ENTRY WITH NO TEXT IS KEPT, unlike an active one. The active list
 * feeds a banner whose whole content is that sentence; the ledger is a history,
 * and dropping an entry would tell the trader they tested fewer hypotheses than
 * they did.
 */
export function leakLedger(
  appFlags: Record<string, unknown> | null | undefined,
): LedgerRule[] {
  const { ledger } = rawLeakLoop(appFlags);
  const out: LedgerRule[] = [];
  for (const raw of ledger) {
    const r = asObject(raw);
    if (!r) continue;
    const status = r['status'] === 'banked' ? 'banked' : 'retired';
    out.push({
      id: str(r['id']),
      leakId: str(r['leakId']),
      ruleText: str(r['ruleText']).trim(),
      createdAt: str(r['createdAt']),
      baseline: null,
      status,
      bankedAt: typeof r['bankedAt'] === 'string' ? r['bankedAt'] : null,
      retiredAt: typeof r['retiredAt'] === 'string' ? r['retiredAt'] : null,
      reason: str(r['reason']),
    });
  }
  // ⚠ Newest first, and the old client reverses the stored order to get there
  // rather than sorting by date — an entry can carry `bankedAt`, `retiredAt` or
  // neither, so there is no single date to sort on. Insertion order is the only
  // thing that is always present.
  return out.reverse();
}

// ---------------------------------------------------------------------------
// Freezing a baseline
// ---------------------------------------------------------------------------

/**
 * The inclusive date extent of a trade set.
 *
 * ⚠ DERIVED FROM THE TRADES, not from either date picker. The old client is
 * explicit about this: the loop's window must not move when the trader changes a
 * filter somewhere else, because a baseline frozen against one population and
 * re-measured against another reports an improvement the trader did not make.
 */
export function leakWindowOf(
  trades: readonly { date: string }[],
): { from: string | null; to: string | null } {
  let from: string | null = null;
  let to: string | null = null;
  for (const t of trades) {
    const d = t?.date;
    if (!d) continue;
    if (from === null || d < from) from = d;
    if (to === null || d > to) to = d;
  }
  return { from, to };
}

/**
 * Freeze the leak's number AND the trader's expectancy, from the same window at
 * the same moment.
 *
 * ⚠ THE GUARDRAIL IS CAPTURED HERE, NOT RECOMPUTED AT PROVE TIME. Measuring it
 * later would compare the rule against a moving target — the trader could not
 * tell a real improvement from a drifting denominator, which is precisely the
 * confusion the guardrail exists to remove.
 *
 * @param trades already narrowed to the baseline window by the caller — the
 *   population is a question for the seam, the same division `proveRule` draws.
 */
export function captureLeakBaseline(
  leakId: string,
  trades: readonly LeakTrade[],
  ctx: LeakContext,
  from: string | null,
  to: string | null,
): LeakBaseline | null {
  const leak = leakById(leakId);
  if (!leak) return null;
  const m = leak.measure(trades, ctx);
  const g = realizedExpectancy(trades);
  return {
    value: m.value,
    n: m.n,
    from,
    to: to ?? '',
    guardrail: { value: g.value, n: g.n },
  };
}

// ---------------------------------------------------------------------------
// Can this leak take a rule at all?
// ---------------------------------------------------------------------------

export interface FixCheck { ok: boolean; reason: string }

/**
 * ⚠ A LEAK THIS CLIENT CANNOT MEASURE MUST NEVER BE OFFERED A RULE, and the
 * refusal belongs here rather than only in the button that opens the form.
 *
 * A rule written against an unmeasurable leak would freeze a null baseline,
 * accrue trades for weeks, and then report "no baseline" forever — the trader
 * would believe they were testing something. Refusing at the engine is what
 * makes that impossible rather than merely unlikely.
 *
 * ⚠ NO LEAK IS CURRENTLY REFUSED HERE. `premature-exit` was, until the post-exit
 * walk was read in; the check stays because it reads `leak.available` rather than
 * a name, so the guard survives the next leak that arrives half-built. The
 * SECOND gate — `baselineUsable` — is what catches a leak that is measurable in
 * principle but has no data yet, which is exactly premature-exit's state on a
 * book whose extensions were never computed.
 */
export function canFixLeak(leakId: string): FixCheck {
  const leak = leakById(leakId);
  if (!leak) return { ok: false, reason: 'That leak does not exist.' };
  if (!leak.available) {
    return {
      ok: false,
      reason: `${leak.label} is not measured in this client yet`
        + `${leak.needs ? ` — it needs ${leak.needs}` : ''}. A rule written `
        + `against it could never be proved.`,
    };
  }
  return { ok: true, reason: '' };
}

// ---------------------------------------------------------------------------
// The three transitions
// ---------------------------------------------------------------------------

export interface CreateRuleInput {
  id: string;
  leakId: string;
  ruleText: string;
  /** ISO date. The lower bound of every Prove window from here on. */
  createdAt: string;
  baseline: LeakBaseline | null;
}

export interface LoopWrite {
  /** The whole flags blob to save, or null when nothing should be written. */
  appFlags: Record<string, unknown> | null;
  error: string | null;
}

/**
 * Start a rule.
 *
 * ⚠ ONE ACTIVE RULE PER LEAK. Writing a rule for a leak that already has one
 * RETIRES the old rule into the ledger rather than dropping it — a hypothesis
 * the trader tested is history, not clutter. Rules on other leaks are untouched,
 * which is the whole reason several can run at once.
 *
 * ⚠ REFUSES AN UNUSABLE BASELINE AT THE ENGINE, matching the old client's own
 * comment. A rule frozen against a baseline that cannot be improved on — a
 * controllable-loss figure already at $0, a capture efficiency already at 100 —
 * would sit there accruing trades and report `no-baseline` forever.
 */
export function createRule(
  appFlags: Record<string, unknown> | null | undefined,
  input: CreateRuleInput,
): LoopWrite {
  const fixable = canFixLeak(input.leakId);
  if (!fixable.ok) return { appFlags: null, error: fixable.reason };

  const text = String(input.ruleText ?? '').trim();
  if (!text) return { appFlags: null, error: 'Write the rule first.' };
  if (!input.id) return { appFlags: null, error: 'The rule has no id.' };

  const usable = baselineUsable(leakById(input.leakId), input.baseline);
  if (!usable.ok) return { appFlags: null, error: usable.reason };

  const { loop, actives, ledger } = rawLeakLoop(appFlags);

  const nextActives: unknown[] = [];
  const nextLedger = [...ledger];
  for (const raw of actives) {
    if (leakIdOf(raw) === input.leakId) {
      const prev = asObject(raw);
      // ⚠ SPREAD, not rebuilt. The retired copy keeps its own frozen baseline
      // and anything else it was carrying — it is the record of what was tested.
      if (prev) {
        nextLedger.push({
          ...prev,
          status: 'retired',
          retiredAt: input.createdAt,
          reason: str(prev['reason']) || 'replaced by a new rule',
        });
      }
      continue;
    }
    nextActives.push(raw);
  }

  nextActives.push({
    id: input.id,
    leakId: input.leakId,
    ruleText: text,
    createdAt: input.createdAt,
    status: 'active',
    baseline: input.baseline === null ? null : {
      value: input.baseline.value,
      n: input.baseline.n,
      from: input.baseline.from,
      to: input.baseline.to,
      guardrail: input.baseline.guardrail === null ? undefined : {
        // ⚠ Written because the old client writes it, and a row it reads back
        // should look identical whichever client made it. Nothing reads it.
        metric: GUARDRAIL_METRIC,
        value: input.baseline.guardrail.value,
        n: input.baseline.guardrail.n,
      },
    },
  });

  return { appFlags: withLoop(appFlags, loop, nextActives, nextLedger), error: null };
}

/**
 * Move a rule out of the active list and into the ledger.
 *
 * ⚠ THE ONLY DIFFERENCE BETWEEN BANKING AND RETIRING IS THE STAMP. Both keep the
 * rule; a retired rule is a hypothesis the trader actually tested, and deleting
 * it would leave them free to re-run the same failed idea next month with no
 * record that they already had.
 */
function closeRule(
  appFlags: Record<string, unknown> | null | undefined,
  ruleId: string,
  stamp: Record<string, unknown>,
): LoopWrite {
  const { loop, actives, ledger } = rawLeakLoop(appFlags);
  const idx = actives.findIndex((raw) => idOf(raw) === ruleId && !!ruleId);
  if (idx < 0) {
    return {
      appFlags: null,
      error: 'That rule is no longer active — it may have been closed in another tab.',
    };
  }
  const prev = asObject(actives[idx]) ?? {};
  const nextActives = actives.filter((_, i) => i !== idx);
  const nextLedger = [...ledger, { ...prev, ...stamp }];
  return { appFlags: withLoop(appFlags, loop, nextActives, nextLedger), error: null };
}

/** Bank a proven habit. `today` is passed in — no clock in `domain/` (D26). */
export function bankRule(
  appFlags: Record<string, unknown> | null | undefined,
  ruleId: string,
  today: string,
): LoopWrite {
  return closeRule(appFlags, ruleId, { status: 'banked', bankedAt: today });
}

/** Retire a rule without banking it. */
export function retireRule(
  appFlags: Record<string, unknown> | null | undefined,
  ruleId: string,
  reason: string,
  today: string,
): LoopWrite {
  return closeRule(appFlags, ruleId, {
    status: 'retired',
    retiredAt: today,
    reason: String(reason ?? '').trim() || 'retired',
  });
}
